/**
 * Per-(staff, location) weekly schedule grid (DNKADM-0053 U2) — the editable 7-row
 * multi-shift schedule for `GET/PUT /v2/admin/staff/{id}/schedule?locationId=…`,
 * seeded from the loaded view and saved through `putStaffSchedule`.
 *
 * Seven rows in `dayOfWeek` order **0=Sunday … 6=Saturday** (the §5.6 staff/resource
 * convention): each row an availability toggle and, when available, 1..n shift rows —
 * each a native `<TextField type="time">` start + end (HH:mm, the LOCATION's local
 * time — staff carry no timezone, §5.6) with add/remove. A single **"Save schedule"**
 * action `PUT`s all seven days at once for the active location.
 *
 * Client validation mirrors the server (`@/utils/shiftValidation`): an available day
 * needs ≥1 shift, each shift `start < end` (overnight disallowed), no overlap/
 * duplicate. A failing day shows its inline error and BLOCKS Save — no PUT reaches the
 * wire while invalid. On a successful save the parent resource is `refetch`ed and a
 * success toast is raised; a validation `400` renders INLINE, anything else raises a
 * non-blocking error toast (`classifyFormError`).
 *
 * Native MUI time inputs only — NO `@mui/x-date-pickers` (not in the fleet baseline).
 *
 * **Read-only mode (DNKADM-0363).** `readOnly` turns the same grid into a view: each
 * day renders its availability and shift times as TEXT, and the toggle, the time
 * inputs, add/remove shift, Copy day and Save all go — hidden, not disabled, because a
 * disabled control says "not now" to someone who is never meant to edit here. The grid
 * is deliberately not forked: My schedule shows a coach their OWN week, and editing it
 * stays on the Staff screen for OWNER/ADMIN (the weekly schedule is ADMIN-only on
 * dink-api, a payroll decision; a coach's own date exceptions are self-service since
 * DNKADM-0375). In read-only mode no state is edited and `putStaffSchedule`
 * is unreachable, so `staffId`/`locationId`/`refetch`/`showToast` are not needed.
 *
 * **A coach's own week (DNKADM-0490).** When the tenant lets coaches set their own hours,
 * My schedule renders this SAME editable grid with two optional seams: `save` replaces
 * the admin `putStaffSchedule` with the self-service `PUT /v2/me/staff/schedule`, and
 * `errorMessage` turns that endpoint's refusal codes into plain copy, shown inline where
 * the validation `400` already renders. Without them the Staff screen's behaviour is
 * unchanged.
 */
import { useState } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { Icon, resolveTokens } from '@/theme';
import { putStaffSchedule, type StaffScheduleDay, type StaffShift } from '@/api/staff';
import { validateShifts } from '@/utils/shiftValidation';
import { copyDay } from '@/utils/scheduleCopy';
import { CopyDayButton } from '@/components/CopyDayButton';
import type { ShowToast } from '@/components/toastContext';
import { classifyFormError } from '@/api/screenError';

/** Sun–Sat day labels indexed by `dayOfWeek` (0=Sunday … 6=Saturday). */
const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Per-day error message, keyed by `dayOfWeek` (0=Sun … 6=Sat). */
type DayErrors = Record<number, string>;

/** A blank shift for a freshly-available day / an added shift row. */
function blankShift(): StaffShift {
  return { startTime: '', endTime: '' };
}

/** Validate the seven days: each available day's shifts checked; closed days pass. */
function validateDays(days: StaffScheduleDay[]): { ok: true } | { ok: false; errors: DayErrors } {
  const errors: DayErrors = {};
  for (const day of days) {
    if (!day.isAvailable) continue;
    const message = validateShifts(day.shifts);
    if (message !== null) errors[day.dayOfWeek] = message;
  }
  return Object.keys(errors).length === 0 ? { ok: true } : { ok: false, errors };
}

/** The loaded weekly schedule (always seven days in `dayOfWeek` 0→6 order). */
interface StaffScheduleGridBaseProps {
  days: StaffScheduleDay[];
}

/** The editing grid (the Staff detail screen) — every write input is required. */
interface StaffScheduleGridEditableProps extends StaffScheduleGridBaseProps {
  readOnly?: false;
  /** The staff id, forwarded on PUT. */
  staffId: string;
  /** The active location id this schedule is authored for, forwarded on PUT. */
  locationId: string;
  /** Re-fetch after a successful save (refetch-after-write). */
  refetch: () => void;
  /** Raise a non-blocking toast (success / upstream-error). */
  showToast: ShowToast;
  /**
   * The write Save performs (DNKADM-0490). Omitted → the admin
   * `PUT /v2/admin/staff/{staffId}/schedule?locationId=…`; My schedule passes the coach's
   * own `PUT /v2/me/staff/schedule`.
   */
  save?: (days: StaffScheduleDay[]) => Promise<void>;
  /**
   * Plain copy for a failed save, shown inline (DNKADM-0490). `null` falls through to
   * the shared `classifyFormError` treatment.
   */
  errorMessage?: (error: unknown) => string | null;
}

/**
 * The viewing grid (My schedule, DNKADM-0363). The write inputs are not merely
 * unused — they are not ACCEPTED, so a read-only caller cannot hand this component a
 * save path it will never take.
 */
interface StaffScheduleGridReadOnlyProps extends StaffScheduleGridBaseProps {
  readOnly: true;
}

type StaffScheduleGridProps = StaffScheduleGridEditableProps | StaffScheduleGridReadOnlyProps;

export function StaffScheduleGrid(props: StaffScheduleGridProps) {
  const readOnly = props.readOnly === true;
  // Edit the seven rows locally; the parent remounts this on each (re)load / location
  // change so the rows reseed from the freshly loaded view.
  const [days, setDays] = useState<StaffScheduleDay[]>(props.days);
  const [dayErrors, setDayErrors] = useState<DayErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function updateDay(dayOfWeek: number, patch: Partial<StaffScheduleDay>) {
    setDays((prev) => prev.map((d) => (d.dayOfWeek === dayOfWeek ? { ...d, ...patch } : d)));
  }

  function handleToggleAvailable(dayOfWeek: number, available: boolean) {
    updateDay(
      dayOfWeek,
      available
        ? { isAvailable: true, shifts: [blankShift()] }
        : { isAvailable: false, shifts: [] },
    );
  }

  function updateShift(dayOfWeek: number, index: number, patch: Partial<StaffShift>) {
    setDays((prev) =>
      prev.map((d) =>
        d.dayOfWeek === dayOfWeek
          ? { ...d, shifts: d.shifts.map((s, i) => (i === index ? { ...s, ...patch } : s)) }
          : d,
      ),
    );
  }

  function addShift(dayOfWeek: number) {
    setDays((prev) =>
      prev.map((d) =>
        d.dayOfWeek === dayOfWeek ? { ...d, shifts: [...d.shifts, blankShift()] } : d,
      ),
    );
  }

  function removeShift(dayOfWeek: number, index: number) {
    setDays((prev) =>
      prev.map((d) =>
        d.dayOfWeek === dayOfWeek ? { ...d, shifts: d.shifts.filter((_s, i) => i !== index) } : d,
      ),
    );
  }

  // Copy one day's availability + (cloned) shifts into the chosen target days,
  // staging the change for the existing whole-week Save (no extra request).
  function copyDayTo(sourceDow: number, targetDows: number[]) {
    setDays((prev) => copyDay(prev, sourceDow, targetDows));
  }

  async function handleSave() {
    // Unreachable in read-only mode (the Save control is not rendered) — the guard is
    // what lets the props union withhold `staffId`/`locationId` from that caller.
    if (props.readOnly === true) return;
    setServerError(null);
    const result = validateDays(days);
    if (!result.ok) {
      setDayErrors(result.errors);
      return;
    }
    setDayErrors({});
    setSaving(true);
    try {
      if (props.save) await props.save(days);
      else await putStaffSchedule(props.staffId, props.locationId, days);
      props.refetch();
      props.showToast('Schedule saved', 'success');
    } catch (err) {
      const mapped = props.errorMessage?.(err) ?? null;
      if (mapped !== null) {
        setServerError(mapped);
        return;
      }
      const view = classifyFormError(err, 'The schedule couldn’t be saved. Please try again.');
      if (view.kind === 'inline') setServerError(view.message);
      else props.showToast(view.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Paper elevation={1} sx={{ px: 2.75, py: 0.75 }}>
        {days.map((day, i) =>
          readOnly ? (
            <ReadOnlyDayRow
              key={day.dayOfWeek}
              label={DAY_LABELS[day.dayOfWeek] ?? `Day ${day.dayOfWeek}`}
              day={day}
              last={i === days.length - 1}
            />
          ) : (
            <DayRow
              key={day.dayOfWeek}
              label={DAY_LABELS[day.dayOfWeek] ?? `Day ${day.dayOfWeek}`}
              dayLabels={DAY_LABELS}
              day={day}
              error={dayErrors[day.dayOfWeek]}
              disabled={saving}
              last={i === days.length - 1}
              onToggleAvailable={(available) => handleToggleAvailable(day.dayOfWeek, available)}
              onShiftChange={(index, patch) => updateShift(day.dayOfWeek, index, patch)}
              onAddShift={() => addShift(day.dayOfWeek)}
              onRemoveShift={(index) => removeShift(day.dayOfWeek, index)}
              onCopyDay={(targetDows) => copyDayTo(day.dayOfWeek, targetDows)}
            />
          ),
        )}
      </Paper>
      {!readOnly && serverError && (
        <Box role="alert" sx={{ mt: 1.5, fontSize: 13, color: 'error.main', fontWeight: 500 }}>
          {serverError}
        </Box>
      )}
      {!readOnly && (
        <Stack
          direction="row"
          sx={{ justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, mt: 1.75 }}
        >
          <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
            {saving ? 'Saving…' : 'Save schedule'}
          </Button>
        </Stack>
      )}
    </Box>
  );
}

/**
 * One day's row as a VIEW (DNKADM-0363): the day label and, beside it, either the
 * day's shift times or "Unavailable". No toggle, no inputs, no add/remove, no Copy
 * day — the read-only grid offers no control at all, so there is nothing to disable.
 */
function ReadOnlyDayRow({
  label,
  day,
  last,
}: {
  label: string;
  day: StaffScheduleDay;
  last: boolean;
}) {
  const t = resolveTokens(useTheme());
  const hours =
    day.isAvailable && day.shifts.length > 0
      ? day.shifts.map((s) => `${s.startTime}–${s.endTime}`).join(', ')
      : 'Unavailable';
  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'baseline',
        gap: 2,
        py: 1.5,
        borderBottom: last ? 'none' : '1px solid',
        borderColor: t.lineFaint,
        flexWrap: 'wrap',
      }}
    >
      <Typography sx={{ fontSize: 13.5, fontWeight: 500, minWidth: 96 }}>{label}</Typography>
      <Typography
        sx={{ fontSize: 13.5, color: day.isAvailable ? 'text.primary' : 'text.secondary' }}
      >
        {hours}
      </Typography>
    </Stack>
  );
}

/** One day's row: the day label + availability toggle, and (when available) shifts. */
function DayRow({
  label,
  dayLabels,
  day,
  error,
  disabled,
  last,
  onToggleAvailable,
  onShiftChange,
  onAddShift,
  onRemoveShift,
  onCopyDay,
}: {
  label: string;
  dayLabels: string[];
  day: StaffScheduleDay;
  error: string | undefined;
  disabled: boolean;
  last: boolean;
  onToggleAvailable: (available: boolean) => void;
  onShiftChange: (index: number, patch: Partial<StaffShift>) => void;
  onAddShift: () => void;
  onRemoveShift: (index: number) => void;
  onCopyDay: (targetDows: number[]) => void;
}) {
  const t = resolveTokens(useTheme());
  return (
    <Box
      sx={{
        py: 2,
        borderBottom: last ? 'none' : '1px solid',
        borderColor: t.lineFaint,
      }}
    >
      <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 500, minWidth: 96, pt: 1 }}>
          {label}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={day.isAvailable}
                onChange={(e) => onToggleAvailable(e.target.checked)}
                disabled={disabled}
                slotProps={{ input: { 'aria-label': `${label} available` } }}
              />
            }
            label={day.isAvailable ? 'Available' : 'Unavailable'}
            sx={{ mb: day.isAvailable ? 1 : 0 }}
          />
          {day.isAvailable &&
            day.shifts.map((shift, index) => (
              <Stack
                // Shift rows are positional; index keys are correct (add/remove rebuilds the list).
                key={index}
                direction="row"
                sx={{ alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}
              >
                <TextField
                  type="time"
                  label="Start"
                  size="small"
                  value={shift.startTime}
                  onChange={(e) => onShiftChange(index, { startTime: e.target.value })}
                  disabled={disabled}
                  error={Boolean(error)}
                  sx={{ width: 140 }}
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: {
                      'aria-label': `${label} shift ${index + 1} start time (HH:mm, local)`,
                    },
                  }}
                />
                <TextField
                  type="time"
                  label="End"
                  size="small"
                  value={shift.endTime}
                  onChange={(e) => onShiftChange(index, { endTime: e.target.value })}
                  disabled={disabled}
                  error={Boolean(error)}
                  sx={{ width: 140 }}
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: {
                      'aria-label': `${label} shift ${index + 1} end time (HH:mm, local)`,
                    },
                  }}
                />
                <IconAction
                  size="small"
                  label={`Remove ${label} shift ${index + 1}`}
                  onClick={() => onRemoveShift(index)}
                  disabled={disabled || day.shifts.length === 1}
                >
                  <Icon name="close" sx={{ fontSize: 18 }} />
                </IconAction>
              </Stack>
            ))}
          {day.isAvailable && (
            <Button
              size="small"
              startIcon={<Icon name="add" sx={{ fontSize: 18 }} />}
              onClick={onAddShift}
              disabled={disabled}
              sx={{ textTransform: 'none' }}
            >
              Add shift
            </Button>
          )}
        </Box>
        <Box sx={{ pt: 0.5 }}>
          <CopyDayButton
            sourceDow={day.dayOfWeek}
            dayLabels={dayLabels}
            disabled={disabled}
            onApply={onCopyDay}
          />
        </Box>
      </Stack>
      {error && (
        <Typography role="alert" sx={{ mt: 0.75, fontSize: 12, color: 'error.main' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
